import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
import { isMobileOnly } from 'react-device-detect';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

import WebcastingContext from '../../context/webcasting/webcastingContext';
import './index.css';

export const TRANSCRIPT_PANEL_W = 360;

const useStyles = makeStyles((theme) => ({
  /* ── panel shell ── */
  panel: {
    position: 'fixed',
    zIndex: 1400,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    transition: 'transform 0.3s ease',
    /* always off until explicitly opened — prevents any click-through when closed */
    pointerEvents: 'none',
    /* desktop: right side panel */
    top: 0,
    right: 0,
    width: TRANSCRIPT_PANEL_W,
    height: '100vh',
    borderLeft: '1px solid #e0e0e0',
    boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
    transform: `translateX(${TRANSCRIPT_PANEL_W}px)`,
    [theme.breakpoints.down('xs')]: {
      /* mobile: bottom sheet */
      top: 'auto',
      bottom: 0,
      left: 0,
      right: 0,
      width: '100%',
      height: '85vh',
      borderLeft: 'none',
      borderTop: '1px solid #e0e0e0',
      borderRadius: '16px 16px 0 0',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
      transform: 'translateY(100%)',
    },
  },
  panelOpen: {
    transform: 'translateX(0)',
    pointerEvents: 'auto',
    [theme.breakpoints.down('xs')]: {
      transform: 'translateY(0)',
    },
  },
  /* (no panelClosed needed — panel base class defaults to off-screen + no pointer events) */

  /* ── mobile backdrop ── */
  backdrop: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      position: 'fixed',
      inset: 0,
      zIndex: 1399,
      backgroundColor: 'rgba(0,0,0,0.4)',
      transition: 'opacity 0.3s ease',
    },
  },
  /* ── drag handle (mobile only) ── */
  handle: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      justifyContent: 'center',
      padding: '10px 0 4px',
      flexShrink: 0,
    },
  },
  handleBar: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#ddd',
  },
  /* ── header ── */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 18px 12px 20px',
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      padding: '8px 14px 10px',
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#111',
    letterSpacing: '-0.01em',
  },
  closeBtn: {
    padding: '4px',
    color: '#555',
    '&:hover': { backgroundColor: '#f0f0f0' },
  },
  /* ── search ── */
  searchWrap: {
    padding: '0 14px 10px',
    flexShrink: 0,
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 14px',
    borderRadius: 100,
    backgroundColor: '#f2f2f2',
    gap: '6px',
  },
  searchIcon: {
    color: '#aaa',
    fontSize: '1.1rem',
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    fontSize: '0.875rem',
    color: '#333',
    '& input::placeholder': { color: '#aaa' },
  },
  clearBtn: {
    padding: '2px',
    color: '#aaa',
    '&:hover': { color: '#555', backgroundColor: 'transparent' },
  },
  resultCount: {
    padding: '0 20px 6px',
    fontSize: '0.72rem',
    color: '#aaa',
    flexShrink: 0,
  },
  /* ── cue list ── */
  cueList: {
    flex: 1,
    overflowY: 'auto',
    padding: '2px 14px 100px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: '#ddd transparent',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: 4 },
  },
  cueItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '11px 14px',
    borderRadius: 10,
    backgroundColor: '#f6f6f6',
    cursor: 'pointer',
    transition: 'background 0.15s',
    outline: 'none',
    userSelect: 'none',
    '&:hover': { backgroundColor: '#ececec' },
    '&:focus-visible': { outline: '2px solid #5b8bd8' },
    [theme.breakpoints.down('xs')]: {
      padding: '12px 12px',
    },
  },
  cueItemActive: {
    backgroundColor: '#dbeafe',
    '&:hover': { backgroundColor: '#c8dffb' },
  },
  cueTime: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#5b8bd8',
    fontFamily: '"SFMono-Regular","Consolas","Liberation Mono",monospace',
    flexShrink: 0,
    paddingTop: 2,
    minWidth: 32,
  },
  cueText: {
    fontSize: '0.875rem',
    color: '#222',
    lineHeight: 1.55,
    flex: 1,
    wordBreak: 'break-word',
  },
  cueTextActive: {
    fontWeight: 600,
    color: '#111',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: '40px 24px',
    color: '#aaa',
    textAlign: 'center',
    gap: '10px',
  },
}));

function msToTimecode(ms) {
  const totalSecs = Math.floor(ms / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function highlightText(text, query) {
  if (!query.trim()) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className='transcript-highlight'>{part}</mark>
      : part
  );
}

const TranscriptDrawer = ({ event, isOpen, onClose }) => {
  const classes = useStyles();
  const { getSeekingTime, playerCurrentTime } = useContext(WebcastingContext);

  const [cues, setCues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const activeItemRef = useRef(null);
  const listRef = useRef(null);
  const userScrolledRef = useRef(false);
  const scrollTimerRef = useRef(null);

  useEffect(() => {
    if (!event?.webcast?.layout?.showTranscript) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchTranscript = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/public/api/subtitle/${event._id}`
        );
        if (!res.ok) {
          if (res.status === 404) throw new Error('No transcript has been uploaded for this event.');
          if (res.status === 400) throw new Error('Invalid event ID.');
          throw new Error('Failed to load transcript. Please try again.');
        }
        const data = await res.json();
        if (!cancelled) setCues(data.cues || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTranscript();
    return () => { cancelled = true; };
  }, [event._id, event?.webcast?.layout?.showTranscript]);

  const activeCueIndex = useCallback(() => {
    if (!cues.length || !playerCurrentTime) return -1;
    const timeMs = playerCurrentTime * 1000;
    return cues.findIndex((c) => timeMs >= c.startMs && timeMs <= c.endMs);
  }, [cues, playerCurrentTime])();

  useEffect(() => {
    if (search.trim()) return;
    if (activeCueIndex === -1) return;
    if (userScrolledRef.current) return;

    const list = listRef.current;
    const item = activeItemRef.current;
    if (!list || !item) return;

    const listRect = list.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    // Only scroll when the active cue is (partly) outside the visible list area
    const isAbove = itemRect.top < listRect.top;
    const isBelow = itemRect.bottom > listRect.bottom;
    if (!isAbove && !isBelow) return;

    // Centre the active cue within the list viewport, scrolling the list only
    const delta =
      (itemRect.top - listRect.top) -
      (list.clientHeight / 2 - item.clientHeight / 2);

    list.scrollTo({ top: list.scrollTop + delta, behavior: 'smooth' });
  }, [activeCueIndex, search]);

  // Only genuine user input (wheel / touch drag) pauses auto-scroll —
  // programmatic smooth scrolling must NOT trip this, or auto-scroll stops.
  const handleUserInteract = useCallback(() => {
    userScrolledRef.current = true;
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      userScrolledRef.current = false;
    }, 5000);
  }, []);

  const handleCueClick = useCallback((cue) => {
    getSeekingTime(cue.startMs / 1000);
    if (isMobileOnly) onClose(); // close sheet on mobile after jumping
  }, [getSeekingTime, onClose]);

  const filteredCues = search.trim()
    ? cues.filter((c) => c.text.toLowerCase().includes(search.toLowerCase()))
    : cues;

  const activeIndex = activeCueIndex;

  const panel = (
    <>
      {/* Mobile backdrop — only when open */}
      {isMobileOnly && isOpen && (
        <div
          className={classes.backdrop}
          style={{ opacity: 1, pointerEvents: 'auto' }}
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        id='transcript-panel'
        className={`${classes.panel} ${classes.panelOpen}`}
        role='complementary'
        aria-label='Transcript panel'
      >
        {/* Mobile drag handle */}
        <div className={classes.handle}>
          <div className={classes.handleBar} />
        </div>

        {/* Header */}
        <div className={classes.header}>
          <Typography className={classes.title}>Transcript</Typography>
          <IconButton className={classes.closeBtn} onClick={onClose} size='small' aria-label='Close transcript'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>

        {/* Search */}
        {!loading && !error && cues.length > 0 && (
          <div className={classes.searchWrap}>
            <div className={classes.searchBar}>
              <SearchIcon className={classes.searchIcon} />
              <InputBase
                className={classes.searchInput}
                placeholder='Search transcript...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{ 'aria-label': 'Search transcript' }}
                fullWidth
              />
              {search && (
                <IconButton className={classes.clearBtn} size='small' disableRipple onClick={() => setSearch('')}>
                  <ClearIcon style={{ fontSize: '0.9rem' }} />
                </IconButton>
              )}
            </div>
          </div>
        )}

        {/* Result count */}
        {search.trim() && !loading && (
          <Typography className={classes.resultCount}>
            {filteredCues.length} result{filteredCues.length !== 1 ? 's' : ''}
          </Typography>
        )}

        {/* Body */}
        {loading ? (
          <Box className={classes.emptyState}>
            <CircularProgress size={28} style={{ color: '#5b8bd8' }} />
            <Typography variant='caption' style={{ color: '#aaa' }}>Loading transcript…</Typography>
          </Box>
        ) : error ? (
          <Box className={classes.emptyState}>
            <Typography variant='body2' style={{ color: '#888' }}>{error}</Typography>
          </Box>
        ) : filteredCues.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant='body2' style={{ color: '#888' }}>
              {search.trim() ? `No results for "${search}"` : 'No transcript cues found.'}
            </Typography>
          </Box>
        ) : (
          <div
            className={classes.cueList}
            ref={listRef}
            onWheel={handleUserInteract}
            onTouchMove={handleUserInteract}
          >
            {filteredCues.map((cue) => {
              const isActive = !search.trim() && cue.index === cues[activeIndex]?.index;
              return (
                <div
                  key={cue.index}
                  ref={isActive ? activeItemRef : null}
                  className={`${classes.cueItem}${isActive ? ` ${classes.cueItemActive}` : ''}`}
                  onClick={() => handleCueClick(cue)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleCueClick(cue)}
                  aria-label={`Jump to ${msToTimecode(cue.startMs)}: ${cue.text}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <span className={classes.cueTime}>{msToTimecode(cue.startMs)}</span>
                  <span className={`${classes.cueText}${isActive ? ` ${classes.cueTextActive}` : ''}`}>
                    {search.trim() ? highlightText(cue.text, search) : cue.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  if (!isOpen) return null;

  return createPortal(panel, document.body);
};

export default React.memo(TranscriptDrawer);
